import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Users, CalendarCheck, Video, PoundSterling, Wrench, BookOpen,
  Crown, ExternalLink, AlertOctagon, Bell, TrendingUp, MapPin, GitBranch, ShieldCheck,
} from "lucide-react";
import axios from "axios";
import AdminTile from "../components/admin/AdminTile";
import CalibrationHealthBanner from "../components/admin/CalibrationHealthBanner";
import UnmatchedInstructorRequestsCard from "../components/admin/UnmatchedInstructorRequestsCard";
import PlatformHealthDot from "../components/admin/PlatformHealthDot";
import GuidanceBlock from "../components/guidance/GuidanceBlock";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * AdminHub — Feb 6, 2026 admin restructure.
 *
 * Owner directive: "Admin should feel like a command centre with
 * clear doors into each area, not a messy top-tab strip."
 *
 * Six big section cards. No content, no tabs, no settings wall.
 * Each card opens a dedicated section page that further subdivides
 * the area into mobile-first tile lists.
 *
 * The legacy 1.8k-line AdminPanel.js still works at /admin/legacy as
 * a safety net while we migrate every individual sub-flow into the
 * new structure (preserves every working admin flow).
 */

const SECTIONS = [
  {
    key: "users",
    to: "/admin/users",
    icon: Users,
    accent: "blue",
    label: "Users",
    desc: "Learners · Instructors · PDIs · roles & access",
  },
  {
    key: "bookings",
    to: "/admin/bookings",
    icon: CalendarCheck,
    accent: "emerald",
    label: "Bookings",
    desc: "Lessons · payments · cancellations",
  },
  {
    key: "reviews",
    to: "/admin/reviews",
    icon: Video,
    accent: "violet",
    label: "Reviews",
    desc: "AI lesson reviews · clip basket · uploads · processing",
  },
  {
    key: "money",
    to: "/admin/money",
    icon: PoundSterling,
    accent: "amber",
    label: "Money",
    desc: "Stripe · subscriptions · payouts · revenue",
  },
  {
    key: "system",
    to: "/admin/system",
    icon: Wrench,
    accent: "rose",
    label: "System",
    desc: "Env · email · upload limits · logs · diagnostics",
  },
  {
    key: "content",
    to: "/admin/content",
    icon: BookOpen,
    accent: "indigo",
    label: "Content",
    desc: "Homework · learning cards · roundabouts · PDI training",
  },
];

// D1 (Feb 27, 2026 exposure batch) — enumerate every admin route
// that doesn't have a primary tile, banner, or section card. Keeps
// the 6-section primary surface intact while ensuring no admin
// page is reachable only by typing the URL.
// See /app/memory/OPERATIONAL_EXPOSURE_MAPPING_V1.md §5.D1.
const OTHER_ADMIN_LINKS = [
  { to: "/admin/badge-queue",           label: "Badge queue" },
  { to: "/admin/beta",                  label: "Beta admin" },
  { to: "/admin/content-library",       label: "Content library" },
  { to: "/admin/curriculum",            label: "Curriculum import" },
  { to: "/admin/email-audit",           label: "Email audit" },
  { to: "/admin/enquiries",             label: "Enquiry intake" },
  { to: "/admin/founder-mode",          label: "Founder mode" },
  { to: "/admin/funnel",                label: "Funnel" },
  { to: "/admin/funnels",               label: "Funnels" },
  { to: "/admin/learner-stories",       label: "Learner stories" },
  { to: "/admin/live-smoke",            label: "Live smoke" },
  { to: "/admin/pricing",               label: "Pricing settings" },
  { to: "/admin/refund-retry",          label: "Refund retry" },
  { to: "/admin/payment-confirmations",  label: "Payment confirmations" },
  { to: "/admin/stripe-connect",        label: "Stripe Connect" },
  { to: "/admin/teaching-excellence",   label: "Teaching excellence" },
  { to: "/admin/unmatched-instructor-requests", label: "Unmatched instructor requests" },
];

export default function AdminHub() {
  const [naCount, setNaCount] = useState(0);
  const [patternsCount, setPatternsCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/admin/needs-attention/count`, { withCredentials: true })
      .then((r) => { if (!cancelled) setNaCount(r.data?.open_total || 0); })
      .catch(() => { /* badge hides when 0 — silent on error */ });
    axios.get(`${API}/api/admin/patterns/counts`, { withCredentials: true })
      .then((r) => { if (!cancelled) setPatternsCount(r.data?.open_total || 0); })
      .catch(() => { /* badge hides when 0 — silent on error */ });
    return () => { cancelled = true; };
  }, []);
  return (
    <div data-testid="admin-hub" className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-slate-950 pb-12">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <Crown className="w-4 h-4 text-amber-600" />
          </div>
          <div className="flex-1">
            <p className="font-extrabold text-slate-900 dark:text-white text-base leading-tight">Admin · Command centre</p>
            <p className="text-[11px] text-slate-500 leading-tight">Pick an area</p>
          </div>
          <PlatformHealthDot />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 pt-5 space-y-3">
        <GuidanceBlock />
        <CalibrationHealthBanner />
        <UnmatchedInstructorRequestsCard />
        <AdminTile
          to="/admin/needs-attention"
          icon={Bell}
          label="Needs Attention"
          desc="Operational intelligence · rule-based · what to do next"
          accent="amber"
          testid="admin-hub-tile-needs-attention"
          badge={naCount}
        />
        <AdminTile
          to="/admin/patterns"
          icon={TrendingUp}
          label="Patterns & Trends"
          desc="Operational memory · what KEEPS happening · trajectories"
          accent="indigo"
          testid="admin-hub-tile-patterns"
          badge={patternsCount}
        />
        <AdminTile
          to="/territory"
          icon={MapPin}
          label="Territory"
          desc="Operational geography · postcode-based · honest map"
          accent="emerald"
          testid="admin-hub-tile-territory"
        />
        <AdminTile
          to="/admin/media-bridge"
          icon={GitBranch}
          label="Media Bridge"
          desc="V3 media · health · stuck jobs · safe recovery · read-only"
          accent="violet"
          testid="admin-hub-tile-media-bridge"
        />
        {/* Temporary LIVE C2 preflight tile (Aug 2026) — deletable with
            /admin/preflight/c2 route + backend routes/preflight_c2.py
            when the C2 preflight window closes. */}
        <AdminTile
          to="/admin/preflight/c2"
          icon={ShieldCheck}
          label="C2 Preflight"
          desc="LIVE readiness check · derivative worker · R2 · Stripe · founder — read-only"
          accent="amber"
          testid="admin-hub-tile-c2-preflight"
        />
        {SECTIONS.map((s) => (
          <AdminTile
            key={s.key}
            to={s.to}
            icon={s.icon}
            label={s.label}
            desc={s.desc}
            accent={s.accent}
            testid={`admin-hub-tile-${s.key}`}
          />
        ))}

        <Link
          to="/admin/client-errors"
          data-testid="admin-hub-client-errors-link"
          className="flex items-center gap-2 mt-2 px-4 py-3 rounded-xl text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
        >
          <AlertOctagon className="w-3.5 h-3.5" />
          <span>Client error reports · trace a user&rsquo;s ERR-XXXXXX</span>
        </Link>

        <section
          data-testid="admin-hub-all-admin-pages"
          className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-800"
        >
          <p className="px-1 mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">
            All admin pages
          </p>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1">
            {OTHER_ADMIN_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`admin-hub-link-${l.to.replace(/^\/admin\//, "")}`}
                className="px-2 py-1.5 rounded-md text-xs text-slate-500 hover:text-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors truncate"
              >
                {l.label}
              </Link>
            ))}
          </div>
        </section>

        <Link
          to="/admin/legacy"
          data-testid="admin-hub-legacy-link"
          className="flex items-center gap-2 mt-6 px-4 py-3 rounded-xl text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          <span>Legacy admin panel · all original tools (safety net)</span>
        </Link>
      </main>
    </div>
  );
}
